// Modal: replay stored events through current triggers.
//
// Single-primary, dry-run-first flow. Operator picks a scope, sees an
// impact preview, hits ONE primary action (Run dry-run). After dry-run
// completes, "Run for real" appears with type-to-confirm — destructive
// step is gated behind explicit acknowledgement, not a stray click.
//
// Three scopes:
//   all    — every event ever stored (storage-stats gives an exact count)
//   range  — date window, datetime-local inputs
//   view   — view-bound (uses replay_all + view_id filter on backend)
//
// Extracted from pages/SourceDetailPage.tsx as part of the file split
// (Apr 2026). ScopeCard co-located here — only consumer is this dialog.

import React, { useEffect, useState } from 'react';
import type { View, Trigger, Replay } from '@datahub/shared';
import type { SourceStorageStats, ReplayMetadataFilterClient } from '../../api';
import { createReplay, createReplayAll, getSourceStorageStats } from '../../api';
import { useToast } from '../Toast';
import { Label, inpStyle } from '../common/atoms';

export function ReplayDialog({ sourceId, views, triggers, onCancel, onLaunched }: {
  sourceId: string;
  views: View[];
  triggers: Trigger[];
  onCancel: () => void;
  onLaunched: (r: Replay) => void;
}) {
  const toast = useToast();
  type Scope = 'all' | 'range' | 'view';
  const now = new Date();
  const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  const [scope, setScope] = useState<Scope>('all');
  const [from, setFrom] = useState(weekAgo.toISOString().slice(0, 16));
  const [to, setTo] = useState(now.toISOString().slice(0, 16));
  const [viewId, setViewId] = useState(views[0]?.view_id ?? '');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [stats, setStats] = useState<SourceStorageStats | null>(null);

  // Object Context P2 metadata facets — narrows the T1 GCS scan up front
  // instead of walking every blob then dropping in the worker. Same
  // backend path the recovery merge wired (replays.ts metadata_filter
  // validation); UI was lost in the monolith → component split and is
  // re-ported here. POST_RECOVERY_TODOS.md item A.
  const [facetsOpen, setFacetsOpen] = useState(false);
  const [depthMax, setDepthMax] = useState<'any' | '0' | '1' | '2' | '3' | '4' | '5'>('any');
  const [originKind, setOriginKind] = useState<'any' | 'input' | 'trigger'>('any');
  const [triggerId, setTriggerId] = useState<string>('');
  const [tagKey, setTagKey] = useState('');
  const [tagValue, setTagValue] = useState('');

  const activeTriggers = triggers.filter(t => t.status === 'active');
  const noTriggers = activeTriggers.length === 0;

  function buildMetadataFilter(): ReplayMetadataFilterClient | undefined {
    const f: ReplayMetadataFilterClient = {};
    if (depthMax !== 'any') f.derivation_depth_max = Number(depthMax);
    if (originKind !== 'any') f.origin_kinds = [originKind];
    if (triggerId) f.trigger_ids = [triggerId];
    if (tagKey && tagValue) f.tags = { [tagKey]: [tagValue] };
    return Object.keys(f).length > 0 ? f : undefined;
  }
  const activeFacetCount = (depthMax !== 'any' ? 1 : 0) + (originKind !== 'any' ? 1 : 0) + (triggerId ? 1 : 0) + (tagKey && tagValue ? 1 : 0);

  useEffect(() => {
    getSourceStorageStats(sourceId).then(setStats).catch(() => setStats(null));
  }, [sourceId]);

  // Disable scope=view when there are no views.
  useEffect(() => {
    if (scope === 'view' && views.length === 0) setScope('all');
  }, [views, scope]);

  // Estimated event count under the chosen scope. For 'all' we know exactly
  // (storage-stats); 'range'/'view' we can't pre-count without a backend
  // probe — show "~unknown" rather than fake a number.
  const estimate = scope === 'all'
    ? (stats?.approximate_event_count ?? null)
    : null;

  async function launch(asDryRun: boolean): Promise<void> {
    setSubmitting(true); setError(null);
    const metadataFilter = buildMetadataFilter();
    try {
      let replay: Replay;
      if (scope === 'all') {
        replay = await createReplayAll(sourceId, asDryRun, undefined, metadataFilter) as unknown as Replay;
      } else if (scope === 'range') {
        const fromIso = new Date(from).toISOString();
        const toIso = new Date(to).toISOString();
        replay = await createReplay(sourceId, { from: fromIso, to: toIso, dry_run: asDryRun, view_id: undefined, metadata_filter: metadataFilter });
      } else {
        // view-bound: still uses replay_all semantics + view filter
        replay = await createReplayAll(sourceId, asDryRun, viewId, metadataFilter) as unknown as Replay;
      }
      onLaunched(replay);
    } catch (err) {
      setError(String(err));
      setSubmitting(false);
    }
  }

  async function confirmRealReplay(): Promise<void> {
    const scopeLabel = scope === 'all'
      ? `all ${estimate?.toLocaleString() ?? '?'} events ever stored`
      : scope === 'range'
        ? `events between ${from} and ${to}`
        : `events matching view "${views.find(v => v.view_id === viewId)?.name ?? ''}"`;
    const ok = await toast.confirm({
      title: 'Run replay for real?',
      message: `Will re-process ${scopeLabel}.\n\n${activeTriggers.length} active trigger${activeTriggers.length === 1 ? '' : 's'} will fire on each event. Idempotent — derivatives won't double. Forward webhooks WILL re-fire (external side effect).\n\nRecommend a dry-run first if you haven't.\n\nType REPLAY to confirm.`,
      destructive: true,
      confirmLabel: 'Run for real',
      requireType: 'REPLAY',
    });
    if (!ok) return;
    await launch(false);
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 50,
      background: 'oklch(0.18 0.01 250 / 0.4)',
      display: 'flex', justifyContent: 'center', alignItems: 'flex-start', paddingTop: 80,
    }} onClick={onCancel}>
      <div onClick={e => e.stopPropagation()} style={{
        width: 'min(560px, calc(100vw - 48px))',
        background: 'var(--bg-elev)', border: '1px solid var(--line)', borderRadius: 12, padding: 0,
        overflow: 'hidden',
      }}>
        <div style={{ padding: '20px 24px 14px' }}>
          <div style={{ fontSize: 16, fontWeight: 600 }}>Replay events</div>
          <div style={{ marginTop: 4, fontSize: 12.5, color: 'var(--ink-2)', lineHeight: 1.5 }}>
            Re-process stored events through your current triggers. Idempotent on event_key — same input + same trigger version produces the same derivative.
          </div>
        </div>

        {/* SCOPE — three cards, only one selectable. */}
        <div style={{ padding: '0 24px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <ScopeCard
            checked={scope === 'all'} onSelect={() => setScope('all')}
            title="Everything ever"
            detail={stats ? `${stats.approximate_event_count.toLocaleString()} events · ${stats.oldest_date ?? '?'} → ${stats.newest_date ?? '?'} · ${stats.by_date.length} day${stats.by_date.length === 1 ? '' : 's'}` : 'loading…'}
            recommended
          />
          <ScopeCard
            checked={scope === 'range'} onSelect={() => setScope('range')}
            title="Date range"
            detail="Replay only events received in a specific time window."
          />
          <ScopeCard
            checked={scope === 'view'} onSelect={() => setScope('view')}
            title="View-bound"
            detail={views.length === 0 ? 'No views on this source yet — create one in the Table tab first.' : `Only events matching a saved view's filter (${views.length} view${views.length === 1 ? '' : 's'} available).`}
            disabled={views.length === 0}
          />
        </div>

        {/* SCOPE-DEPENDENT INPUTS */}
        {scope === 'range' && (
          <div style={{ padding: '0 24px 14px', display: 'flex', flexDirection: 'column', gap: 6 }}>
            <Label>From (UTC, inclusive)</Label>
            <input type="datetime-local" value={from} onChange={e => setFrom(e.target.value)} style={inpStyle} />
            <Label>To (UTC, exclusive)</Label>
            <input type="datetime-local" value={to} onChange={e => setTo(e.target.value)} style={inpStyle} />
          </div>
        )}
        {scope === 'view' && views.length > 0 && (
          <div style={{ padding: '0 24px 14px' }}>
            <Label>View</Label>
            <select value={viewId} onChange={e => setViewId(e.target.value)} style={inpStyle}>
              {views.map(v => <option key={v.view_id} value={v.view_id}>{v.name}</option>)}
            </select>
          </div>
        )}

        {/* METADATA FACETS — Object Context P2 narrowing.
            Collapsed by default since most replays don't need them. */}
        <div style={{ margin: '0 24px 14px' }}>
          <button
            type="button"
            onClick={() => setFacetsOpen(o => !o)}
            style={{
              width: '100%', textAlign: 'left',
              padding: '8px 12px', background: 'transparent',
              border: '1px solid var(--line)', borderRadius: 6,
              fontFamily: 'inherit', fontSize: 12.5, color: 'var(--ink-2)',
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
            }}
            aria-expanded={facetsOpen}
          >
            <span style={{ flex: 1 }}>{facetsOpen ? '▾' : '▸'} Metadata facets {activeFacetCount > 0 && <span style={{ fontFamily: 'var(--mono)', fontSize: 10.5, color: 'var(--accent)', background: 'var(--accent-soft)', padding: '1px 6px', borderRadius: 3, marginLeft: 4 }}>{activeFacetCount} active</span>}</span>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 10.5, color: 'var(--ink-3)' }}>narrows the GCS scan</span>
          </button>
          {facetsOpen && (
            <div style={{ marginTop: 8, padding: 12, background: 'var(--bg-sunken)', border: '1px solid var(--line)', borderRadius: 6, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div>
                  <Label>Derivation depth ≤</Label>
                  <select value={depthMax} onChange={e => setDepthMax(e.target.value as typeof depthMax)} style={inpStyle}>
                    <option value="any">any</option>
                    <option value="0">0 (input only)</option>
                    <option value="1">1</option>
                    <option value="2">2</option>
                    <option value="3">3</option>
                    <option value="4">4</option>
                    <option value="5">5</option>
                  </select>
                </div>
                <div>
                  <Label>Origin kind</Label>
                  <select value={originKind} onChange={e => setOriginKind(e.target.value as typeof originKind)} style={inpStyle}>
                    <option value="any">any</option>
                    <option value="input">input only</option>
                    <option value="trigger">trigger derivative only</option>
                  </select>
                </div>
              </div>
              <div>
                <Label>Trigger</Label>
                <select value={triggerId} onChange={e => setTriggerId(e.target.value)} style={inpStyle}>
                  <option value="">— any —</option>
                  {triggers.map(t => (
                    <option key={t.trigger_id} value={t.trigger_id}>{t.name} (v{t.version})</option>
                  ))}
                </select>
                <div style={{ marginTop: 4, fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--ink-3)' }}>Only events whose origin trigger matches will be replayed.</div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div>
                  <Label>Tag key</Label>
                  <input value={tagKey} onChange={e => setTagKey(e.target.value)} placeholder="env" style={inpStyle} />
                </div>
                <div>
                  <Label>Tag value</Label>
                  <input value={tagValue} onChange={e => setTagValue(e.target.value)} placeholder="prod" style={inpStyle} />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* IMPACT PREVIEW */}
        <div style={{ margin: '0 24px 14px', padding: '12px 14px', background: 'var(--bg-sunken)', border: '1px solid var(--line)', borderRadius: 6 }}>
          <div style={{ fontSize: 11, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-3)', marginBottom: 6 }}>What will happen</div>
          <ul style={{ margin: 0, padding: '0 0 0 18px', fontSize: 12.5, color: 'var(--ink-2)', lineHeight: 1.6 }}>
            <li>{estimate !== null ? <strong>{estimate.toLocaleString()}</strong> : '~all'} input events scanned from durable storage</li>
            {activeFacetCount > 0 && <li>filtered by <strong>{activeFacetCount}</strong> metadata facet{activeFacetCount === 1 ? '' : 's'} — only matching events fire triggers</li>}
            <li>{activeTriggers.length === 0 ? <strong style={{ color: 'var(--danger)' }}>No active triggers — nothing fires</strong> : <><strong>{activeTriggers.length}</strong> active trigger{activeTriggers.length === 1 ? '' : 's'} fire{activeTriggers.length === 1 ? 's' : ''} per event</>}</li>
            {activeTriggers.some(t => t.action.type === 'promote_to_table') && <li>BQ rows MERGE in (idempotent — duplicate keys overwrite, not double-insert)</li>}
            {activeTriggers.some(t => t.action.type === 'forward_webhook') && <li style={{ color: 'oklch(0.55 0.15 75)' }}>forward_webhook triggers WILL re-fire (external side effect)</li>}
          </ul>
        </div>

        {noTriggers && (
          <div style={{ margin: '0 24px 14px', padding: '10px 12px', background: 'oklch(0.95 0.06 75 / 0.8)', border: '1px solid oklch(0.78 0.10 75)', borderRadius: 6, fontSize: 12, color: 'var(--ink)' }}>
            This source has 0 active triggers. Replay will count events but nothing will land in BQ. Add a trigger first if you want a visible result.
          </div>
        )}

        {error && <div style={{ margin: '0 24px 12px', padding: '8px 12px', background: 'oklch(0.95 0.04 25)', border: '1px solid var(--danger)', borderRadius: 6, color: 'var(--danger)', fontSize: 12 }}>{error}</div>}

        {/* ACTIONS — single primary path: dry-run first. "Run for real" is
            secondary, gated behind a confirm dialog. */}
        <div style={{ padding: '14px 24px 18px', borderTop: '1px solid var(--line)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <button className="btn ghost" onClick={onCancel} disabled={submitting}>Cancel</button>
          <div style={{ flex: 1 }} />
          <button
            className="btn ghost"
            onClick={() => { void confirmRealReplay(); }}
            disabled={submitting}
            style={{ color: 'var(--ink-2)' }}
            title="Skip dry-run and run for real (with confirm)"
          >Run for real…</button>
          <button
            className="btn primary"
            onClick={() => { void launch(true); }}
            disabled={submitting}
            title="Recommended: count what would happen without firing any triggers"
          >{submitting ? 'Launching…' : 'Run dry-run'}</button>
        </div>
      </div>
    </div>
  );
}

/** Single scope row in the Replay dialog. Radio-card visual: checkmark
 *  + title + mono detail line. Disabled state grays out. Co-located here
 *  because ReplayDialog is the only consumer. */
function ScopeCard({ checked, onSelect, title, detail, recommended, disabled }: {
  checked: boolean;
  onSelect: () => void;
  title: string;
  detail: string;
  recommended?: boolean;
  disabled?: boolean;
}): React.ReactElement {
  return (
    <button
      type="button"
      onClick={() => { if (!disabled) onSelect(); }}
      disabled={disabled}
      style={{
        textAlign: 'left',
        padding: '10px 14px',
        background: checked ? 'var(--accent-soft)' : 'var(--bg-sunken)',
        border: `1px solid ${checked ? 'var(--accent-line)' : 'var(--line)'}`,
        borderRadius: 6,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        display: 'flex', alignItems: 'flex-start', gap: 10,
        transition: 'background 100ms, border 100ms',
      }}
    >
      <div style={{
        width: 14, height: 14, marginTop: 2, borderRadius: '50%',
        border: `2px solid ${checked ? 'var(--accent)' : 'var(--ink-3)'}`,
        background: checked ? 'var(--accent)' : 'transparent',
        boxShadow: checked ? 'inset 0 0 0 2px var(--bg-sunken)' : 'none',
        flexShrink: 0,
      }} />
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink)' }}>{title}</span>
          {recommended && <span style={{ fontSize: 10, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--accent)', background: 'var(--accent-soft)', padding: '1px 5px', borderRadius: 3 }}>recommended</span>}
        </div>
        <div style={{ marginTop: 2, fontSize: 11.5, fontFamily: 'var(--mono)', color: 'var(--ink-3)' }}>{detail}</div>
      </div>
    </button>
  );
}
